import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FiCpu, FiDatabase, FiLayout, FiServer, FiTrendingUp } from 'react-icons/fi';

const Section = styled.section`
  padding: 8rem 0;
  position: relative;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    &::before {
      content: '01.';
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.25rem;
      color: ${props => props.theme.colors.primary};
    }
    
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${props => props.theme.colors.border};
      margin-left: 2rem;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 4rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const TextContent = styled(motion.div)`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textDim};
  line-height: 1.9;
  
  p {
    margin-bottom: 1.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    strong {
      color: ${props => props.theme.colors.primary};
      font-weight: 500;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
`;

const iconFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 1.75rem;
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s;
  }
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.surfaceLight};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.4);
    
    &::before {
      transform: scaleX(1);
    }
    
    .icon {
      animation: ${iconFloat} 1s ease-in-out infinite;
      color: ${props => props.theme.colors.primary};
    }
  }
  
  .icon {
    font-size: 1.75rem;
    color: ${props => props.theme.colors.textDim};
    margin-bottom: 1rem;
    transition: all 0.4s;
  }
  
  h3 {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  p {
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textDim};
    line-height: 1.5;
  }
`;

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const stats = [
    {
      icon: <FiCpu />,
      title: "Automation",
      desc: "자동화 프로세스를 구축\n 연간 5억원의 비용 절감"
    },
    {
      icon: <FiDatabase />,
      title: "Data Analysis",
      desc: "비정형 데이터 수집 파이프라인 구축 인사이트 대시보드 구현"
    },
    {
      icon: <FiServer />,
      title: "Scalable Backend",
      desc: "비동기 서버 구축 및 수동 프로세스의 웹 시스템화"
    },
    {
      icon: <FiTrendingUp />,
      title: "AI Strategy",
      desc: "LLM Text-to-SQL \n에이전트 구축 및\n데이터 리드타임 80% 단축"
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <Section id="about">
      <Container ref={ref}>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>About Me</h2>
        </SectionHeader>
        
        <Grid>
          <TextContent
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p>
              안녕하세요, <strong>자동화 엔지니어 하율</strong>입니다.<br />
              지난 5년간 다양한 산업 분야를 경험하며 현장의 문제점을 발견했고,
              <br />근본적으로 해결해보고자 개발자로의 커리어 전환을 결심했습니다.
            </p>
            <p>
              빅데이터 전문가 과정과 컴퓨터공학 전공을 통해 데이터 분석 및 개발 역량을 쌓았으며,<br />
              현재는 <strong>쿠팡이츠서비스 Business Automation</strong> 팀에서 <br />Automation Engineer로 재직중입니다.
            </p>
            <p>
            기술적 구현 이전에 비즈니스를 이해하고 현장 상황에 적합한 자동화 환경을 구축합니다.<br />
            수동적으로 요구사항을 받는 것이 아닌 실무자의 잠재적 니즈를 찾아 개선 아이디어를 논의하고
            <strong>업무 효율을 향상</strong>시킬 수 있는 엔지니어 입니다.
            </p>
          </TextContent>
          
          <StatsGrid
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {stats.map((stat, index) => (
              <StatCard 
                key={index}
                variants={itemVariants}
              >
                <div className="icon">{stat.icon}</div>
                <h3>{stat.title}</h3>
                <p>{stat.desc}</p>
              </StatCard>
            ))}
          </StatsGrid>
        </Grid>
      </Container>
    </Section>
  );
};

export default About;
